import React from 'react';
import { Chip, Tooltip, useTheme } from '@mui/material';
import { 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  Gavel,
  Users,
  CalendarClock,
  Video,
  Bell,
  Calendar
} from 'lucide-react';

interface CalendarEvent {
  type: 'task' | 'court-date' | 'meeting' | 'deadline' | 'hearing' | 'deposition' | 'reminder' | 'other';
  title: string;
  status?: string;
  priority?: string;
}

interface CalendarEventProps {
  event: CalendarEvent;
  isMobile?: boolean;
}

export const CalendarEvent: React.FC<CalendarEventProps> = ({ 
  event,
  isMobile,
}) => {
  const theme = useTheme();

  const getEventColor = () => {
    if (event.type === 'task') {
      switch (event.priority) {
        case 'high':
          return theme.palette.error;
        case 'medium':
          return theme.palette.warning;
        default:
          return theme.palette.success;
      }
    }

    switch (event.type) {
      case 'court-date':
      case 'hearing':
        return theme.palette.error;
      case 'meeting':
      case 'reminder':
        return theme.palette.info;
      case 'deadline':
        return theme.palette.warning;
      case 'deposition':
        return theme.palette.secondary;
      default:
        return theme.palette.grey;
    }
  };

  const getEventIcon = () => {
    if (event.type === 'task') {
      switch (event.status) {
        case 'completed':
          return <CheckCircle size={isMobile ? 12 : 14} />;
        case 'blocked':
          return <AlertTriangle size={isMobile ? 12 : 14} />;
        default:
          return <Clock size={isMobile ? 12 : 14} />;
      }
    }

    switch (event.type) {
      case 'court-date':
      case 'hearing':
        return <Gavel size={isMobile ? 12 : 14} />;
      case 'meeting':
        return <Users size={isMobile ? 12 : 14} />;
      case 'deadline':
        return <CalendarClock size={isMobile ? 12 : 14} />;
      case 'deposition':
        return <Video size={isMobile ? 12 : 14} />;
      case 'reminder':
        return <Bell size={isMobile ? 12 : 14} />;
      default:
        return <Calendar size={isMobile ? 12 : 14} />;
    }
  };

  return (
    <Tooltip title={event.title} arrow>
      <Chip
        icon={getEventIcon()}
        label={isMobile ? event.title.substring(0, 12) + '...' : event.title}
        size="small"
        color={event.type === 'court-date' ? 'error' : 'default'}
        variant={event.type === 'court-date' ? 'filled' : 'outlined'}
        sx={{
          width: '100%',
          height: isMobile ? 20 : 24,
          '& .MuiChip-label': {
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            px: 1,
            fontSize: isMobile ? '0.675rem' : '0.75rem',
          },
          '& .MuiChip-icon': {
            ml: 1,
            mr: 0,
          },
        }}
        component="span"
      />
    </Tooltip>
  );
};